/**
 * Paced dice-tiebreak presentation. The server holds ONE long TIEBREAK window and
 * broadcasts the resolved rolls; the client plays an ordered sequence so the room
 * can follow it:
 *
 *   intro     → who's tied, per category (Tied for the Win / Tied for Worst)
 *   countdown → a 3-2-1 like the round start
 *   throw     → the 3D dice are tossed from the tied players' seats (Dice.tsx)
 *   results   → a panel per category listing each player's roll + who advanced
 *
 * `TiebreakDirector` drives the beat (store `tiebreakBeat`) with local timers;
 * `TiebreakPanels` renders the DOM. Both the panels and the 3D dice read the beat.
 */

import { useEffect, useRef, useState } from 'react';
import { HOUSE_ID, type RosterEntry } from '@blackjack/shared';
import { useGameStore } from '../state/store';
import type { DiceSnapshot, TiebreakBeat } from '../state/store';
import { playSfx } from '../audio/sfx';

/** Sub-beat schedule (ms from TIEBREAK entry). Total < server TIEBREAK_MS (13s). */
const BEAT_AT = { countdown: 3000, throw: 6000, results: 9500 } as const;

function nameOf(roster: readonly RosterEntry[], id: string): string {
  if (id === HOUSE_ID) return 'The House';
  return roster.find((r) => r.id === id)?.name ?? 'Someone';
}

/** Drives the tiebreak sub-beats while the server holds the TIEBREAK phase. */
export function TiebreakDirector() {
  const phase = useGameStore((s) => s.server.phase);

  useEffect(() => {
    if (phase !== 'TIEBREAK') return;
    const set = (b: TiebreakBeat): void => useGameStore.getState().setTiebreakBeat(b);
    set('intro');
    const timers = [
      setTimeout(() => set('countdown'), BEAT_AT.countdown),
      setTimeout(() => set('throw'), BEAT_AT.throw),
      setTimeout(() => set('results'), BEAT_AT.results),
    ];
    return () => {
      timers.forEach(clearTimeout);
      set('idle');
    };
  }, [phase]);

  return null;
}

/** 3-2-1 during the countdown beat. */
function TieCountdown() {
  const [n, setN] = useState(3);
  useEffect(() => {
    setN(3);
    playSfx('countdownBeep');
    const iv = setInterval(() => {
      setN((x) => Math.max(1, x - 1));
      playSfx('countdownBeep');
    }, 1000);
    return () => clearInterval(iv);
  }, []);
  return <div className="tiebreak__count title-gold">{n}</div>;
}

/** One category card: the tied players, plus their rolls + winner once revealed. */
function CategoryCard({
  roll,
  showResults,
}: {
  readonly roll: DiceSnapshot;
  readonly showResults: boolean;
}) {
  const roster = useGameStore((s) => s.server.roster);
  const label = roll.slot === 'winner' ? 'Tied for the Win' : 'Tied for Worst (hosts)';

  // First-round roll per contestant (the throw everyone sees); winner = advanced.
  const firstRound = roll.rounds[0];
  const rollById = new Map<string, number>();
  if (firstRound) firstRound.contestants.forEach((id, i) => rollById.set(id, firstRound.rolls[i] ?? 0));

  return (
    <div className={`tiebreak__card tiebreak__card--${roll.slot}`}>
      <h3 className="tiebreak__cardtitle heading-gold">{label}</h3>
      <ul className="tiebreak__list">
        {roll.contestants.map((id) => {
          const won = showResults && id === roll.winnerId;
          return (
            <li key={id} className={`tiebreak__row ${won ? 'is-winner' : ''}`}>
              <span className="tiebreak__name">{nameOf(roster, id)}</span>
              {showResults && (
                <span className="tiebreak__roll numeric">🎲 {rollById.get(id) ?? '—'}</span>
              )}
              {won && <span className="tiebreak__tag">✓ advances</span>}
            </li>
          );
        })}
      </ul>
      {showResults && roll.rounds.length > 1 && (
        <p className="tiebreak__reroll">(re-rolled a tie)</p>
      )}
    </div>
  );
}

export function TiebreakPanels() {
  const phase = useGameStore((s) => s.server.phase);
  const beat = useGameStore((s) => s.tiebreakBeat);
  const diceRolls = useGameStore((s) => s.server.diceRolls);
  // Stable category order: winner card first, then worst-loser.
  const ordered = useRef<DiceSnapshot[]>([]);
  if (diceRolls.length) {
    ordered.current = [...diceRolls].sort((a, b) => (a.slot === 'winner' ? -1 : 1) - (b.slot === 'winner' ? -1 : 1));
  }

  if (phase !== 'TIEBREAK' || beat === 'idle' || diceRolls.length === 0) return null;

  const heading =
    beat === 'results' ? 'Tie-break — results' : beat === 'throw' ? 'Rolling…' : 'Tie-break';

  return (
    <div className="tiebreak">
      <div className="tiebreak__header heading-gold">{heading}</div>
      {beat === 'countdown' ? (
        <TieCountdown />
      ) : (
        <div className="tiebreak__cards">
          {ordered.current.map((roll) => (
            <CategoryCard key={roll.slot} roll={roll} showResults={beat === 'results'} />
          ))}
        </div>
      )}
    </div>
  );
}
